import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import LostLeadsReportController from '@mm/state/controllers/LostLeadsReportController';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { AnalyzeWebsiteTrafficProps } from '@mm/state/modules/modalsState/props';
import { MODALS_TYPE } from '@mm/state/modules/modalsState/types';
import { trafficLoadedSelector } from '@mm/state/selectors/websiteReportSummarySelector';

import {
  Payload,
  useEmailInput,
} from '../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { MIN_DELAY } from './constants';
import { useSignUpFormActions } from '@mm/state/actions/SignUpFormActions';

export const useAnalyzeWebsiteTraffic = ({
  website,
  campaign,
}: AnalyzeWebsiteTrafficProps) => {
  const { setEmail } = useSignUpFormActions();
  const [showCloseByTimer, setShowCloseByTimer] = useState(false);
  const { emailState, onChange } = useEmailInput('');
  const [submitted, setSubmitted] = useState(false);

  const trafficLoaded = useSelector(trafficLoadedSelector);
  const { closeModal } = useModalActions();

  const trafficLoadedRef = useRef(trafficLoaded);
  trafficLoadedRef.current = trafficLoaded; // Needed as setTimeout is not updated with new value
  const showCloseButtonTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null> =
    useRef(null);

  const close = useCallback(() => {
    closeModal(MODALS_TYPE.ANALYZE_WEBSITE_TRAFFIC_MODAL);
  }, [closeModal]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmail(emailState.email);
    const kind = campaign ? 'apollo' : 'email';
    LostLeadsReportController.createReport(website, kind, {
      ...(campaign || {}),
      email: emailState.email,
    });
    setSubmitted(true);
    close();
  };

  const onEmailChange = (e: Payload) => {
    onChange(e);
  };

  const onCloseClick = () => {
    if (!submitted && campaign) {
      LostLeadsReportController.createReport(website, 'apollo', campaign);
    }
    close();
  };

  useEffect(() => {
    showCloseButtonTimeoutRef.current = setTimeout(() => {
      setShowCloseByTimer(true);
    }, MIN_DELAY + 100); // 100ms to remove flickering

    return () => {
      if (showCloseButtonTimeoutRef.current) {
        clearTimeout(showCloseButtonTimeoutRef.current);
      }
    };
  }, []);

  return {
    emailState,
    onCloseClick,
    onEmailChange,
    onSubmit,
    showCloseByTimer,
    trafficLoaded,
  };
};
